import { WEB_NOVEL_CHAPTER_DTO } from '@api/fragment/webNovelChapters';
import {
  ENABLE_PURCHASE_CHAPTER_DTO,
  WEB_NOVEL_LAST_PLAY_INFO_DTO,
} from '@api/fragment/webNovelStory';
import { gql } from '@apollo/client';
import {
  STORY_DETAIL_SEO,
  STORY_DETAIL_HEADER,
  STORY_DETAIL_SUMMARY,
  STORY_DETAIL_BUTTON,
  STORY_DETAIL_POINTS,
  STORY_DETAIL_ENDING_LIST,
  STORY_DETAIL_CHAPTER_LIST,
  STORY_DETAIL_MY_ENDING,
  BADGE_DTO,
  REACHED_ENDING_DTO,
  USER_HAS_ENDING_DTO,
  ITEM_DTO,
  STORY_CHARACTERS,
  COMMENT_DTO,
} from './fragment/story';
import {
  CHAPTER_PURCHASES_DTO,
  CHOICE_RESULT,
  CURRENT_PLAYING_CHAPTER,
  NEXT_CHAPTER_DTO,
} from './fragment/chapters';

export const STORY_DETAIL_SERVER = gql`
  query StoryDetailServer($data: SingleIDInput!) {
    seo: getStoryV3(data: $data) {
      ...StoryDetailSEO
    }
    header: getStoryV3(data: $data) {
      ...StoryDetailHeader
    }
    points: getStoryV3(data: $data) {
      ...StoryDetailPoints
    }
  }
  ${STORY_DETAIL_SEO}
  ${STORY_DETAIL_HEADER}
  ${STORY_DETAIL_POINTS}
`;

// 타입 확인은 getInteractiveStoryDetail.ts파일에서 가능합니다.
// 필드 추가 & 타입 추가 시 반드시 타입 확인 후 추가해주세요
export const STORY_DETAIL_CLIENT = gql`
  query StoryDetail($data: SingleIDInput!) {
    button: getStoryV3(data: $data) {
      ...StoryDetailButton
    }
    endingList: getStoryV3(data: $data) {
      ...StoryDetailEndingList
    }
    chapterList: getStoryV3(data: $data) {
      ...StoryDetailChapterList
    }
    myEnding: getStoryV3(data: $data) {
      ...StoryDetailMyEnding
    }
    storyCharacters: getStoryV3(data: $data) {
      ...StoryCharacters
    }
    summary: getStoryV3(data: $data) {
      ...StoryDetailSummary
    }
  }
  ${STORY_DETAIL_BUTTON}
  ${STORY_DETAIL_ENDING_LIST}
  ${STORY_DETAIL_CHAPTER_LIST}
  ${STORY_DETAIL_MY_ENDING}
  ${STORY_CHARACTERS}
  ${STORY_DETAIL_SUMMARY}
`;

export const WEB_NOVEL_STORY_DETAIL_CLIENT = gql`
  query StoryDetail($data: SingleIDInput!) {
    summary: getStoryV3(data: $data) {
      ...StoryDetailSummary
    }
    enablePurchasedChapterInfo: getStoryV3(data: $data) {
      ...EnablePurchaseChapterDto
    }
    lastPlayInfo: getStoryV3(data: $data) {
      ...WebNovelLastPlayInfoDto
    }
    imageInfo: getStoryV3(data: $data) {
      introImageFile {
        link
      }
      mainImageFile {
        link
      }
    }
  }
  ${STORY_DETAIL_SUMMARY}
  ${WEB_NOVEL_LAST_PLAY_INFO_DTO}
  ${ENABLE_PURCHASE_CHAPTER_DTO}
  ${WEB_NOVEL_CHAPTER_DTO}
`;

export const CHAPTER_PURCHASES = gql`
  query ChapterPurchases($data: SingleIDInput!) {
    user: getUser {
      isPremium
    }
    story: getStoryV3(data: $data) {
      ...ChapterPurchasesDto
    }
  }
  ${CHAPTER_PURCHASES_DTO}
`;

/**
 * 현재 플레이 중인 챕터에서 유저 속성을 업데이트 한다.
 * 현재 유저 속성을 클라이언트로 부터 받은 값으로 덮어쓴다.
 *
 *  현재 플레이중 챕터에서 선택을 내린다.
 *
 * updateUserProps : 업데이트된 Props (JSON encoded), null 이거나 없으면 업데이트하지 않음. ㄴ
 */
export const UPDATE_USER_PROPS = gql`
  mutation updateUserProps(
    $storyId: Int!
    $chapterId: Int!
    $updatedUserProps: String!
  ) {
    updateUserProps(
      data: {
        storyId: $storyId
        chapterId: $chapterId
        updatedUserProps: $updatedUserProps
      }
    ) {
      ok
    }
  }
`;

/**
 * 작품에 연동된 업적 이벤트를 전송합니다.
 *
 * @params storyId, event 작품에 이벤트를 발생시킬 때 필요한 입력값
 */
export const SEND_ACHIEVEMENT_EVENT = gql`
  mutation SendAchievementEvent($storyId: Int!, $event: String!) {
    sendAchievementEvent(data: { storyId: $storyId, event: $event }) {
      success
      badgesAcquired {
        ...BadgeDto
      }
    }
  }
  ${BADGE_DTO}
`;

/**
 * 사용자의 작품 진행 항목을 생성합니다.
 *
 * @params storyId, isRestarting, isRestarting, firstName, lastName, willUseCoin, profileImageId, popupProps
 * 신규로 작품 시작 시 필요한 데이터
 * 캐릭터 생성 완료 시 호출
 */

export const START_STORY = gql`
  mutation StartStory(
    $storyId: Int!
    $isRestarting: Boolean!
    $firstName: String!
    $lastName: String
    $willUseCoin: Boolean!
    $profileImageId: Int
    $popupProps: JSONObject
    $customProfileFileInfo: CustomProfileFileInfo
    $otherCharacterInfoList: [OtherCharacterInfo!]
    $playerClassId: Int
    $storygameSectionId: Int
  ) {
    startStory(
      data: {
        storyId: $storyId
        isRestarting: $isRestarting
        firstName: $firstName
        lastName: $lastName
        willUseCoin: $willUseCoin
        profileImageId: $profileImageId
        popupProps: $popupProps
        customProfileFileInfo: $customProfileFileInfo
        otherCharacterInfoList: $otherCharacterInfoList
        playerClassId: $playerClassId
        storygameSectionId: $storygameSectionId
      }
    ) {
      userId
      firstName
      lastName
      currentPlayingChapter {
        ...CurrentPlayingChapter
      }
      playedChapters {
        chapterId
        playingStoryId
        ending {
          name
          endingId
          chapterId
        }
        currentItems
        chapter {
          chapterIndex
        }
      }
    }
  }
  ${CURRENT_PLAYING_CHAPTER}
`;

/**
 * 현재 플레이 중인 스토리에서 다음 챕터를 플레이한다.
 *
 * @params storyId, chapterId, isRestarting, willUseCoin
 * 스토리에서 다음 챕터 플레이를 하기 위한 input
 */
export const START_CHAPTER = gql`
  mutation StartChapter(
    $storyId: Int!
    $chapterId: Int!
    $isRestarting: Boolean!
    $willUseCoin: Boolean!
    $storygameSectionId: Int
  ) {
    startChapter(
      data: {
        storyId: $storyId
        chapterId: $chapterId
        isRestarting: $isRestarting
        willUseCoin: $willUseCoin
        storygameSectionId: $storygameSectionId
      }
    ) {
      chapterId
      coinUsed
      chapter {
        story {
          lastPlay {
            userId
            firstName
            lastName
            currentPlayingChapter {
              ...CurrentPlayingChapter
            }
            playedChapters {
              chapterId
              playingStoryId
              ending {
                name
                endingId
                chapterId
              }
              currentItems
              chapter {
                chapterIndex
              }
            }
          }
        }
      }
    }
  }
  ${CURRENT_PLAYING_CHAPTER}
`;

export const MAKE_CHOICE = gql`
  mutation MakeChoice(
    $storyId: Int!
    $chapterId: Int!
    $choiceName: String!
    $choice: Int!
    $updatedUserProps: String
    $updatedItems: [String!]
    $event: String
    $numParam1: Int
    $numParam2: Int
    $updatedUserItems: JSONObject
    $showStatistics: Boolean
    $leftUserInputText: String
    $rightUserInputText: String
  ) {
    makeChoice(
      data: {
        storyId: $storyId
        chapterId: $chapterId
        choiceName: $choiceName
        choice: $choice
        updatedUserProps: $updatedUserProps
        updatedItems: $updatedItems
        event: $event
        numParam1: $numParam1
        numParam2: $numParam2
        updatedUserItems: $updatedUserItems
        showStatistics: $showStatistics
        leftUserInputText: $leftUserInputText
        rightUserInputText: $rightUserInputText
      }
    ) {
      ok
      storyItems {
        ...ItemDto
      }
      achievement {
        success
        error
        badgesAcquired {
          ...BadgeDto
        }
      }
      choiceResult {
        ...ChoiceResult
      }
      userSelectedChoice {
        choiceId
        selectedChoice
        leftUserInputText
        rightUserInputText
      }
    }
  }
  ${ITEM_DTO}
  ${BADGE_DTO}
  ${CHOICE_RESULT}
`;

export const REACHED_ENDING = gql`
  mutation ReachedEnding(
    $storyId: Int!
    $chapterId: Int!
    $endingName: String!
    $customEndingName: String
    $customEndingDescription: String
  ) {
    reachedEnding(
      data: {
        storyId: $storyId
        chapterId: $chapterId
        endingName: $endingName
        customEndingName: $customEndingName
        customEndingDescription: $customEndingDescription
      }
    ) {
      ending {
        chapterId
        ...ReachedEndingDto
      }
      collectedEnding {
        ...UserHasEndingDto
      }
      nextChapter {
        ...NextChapterDto
      }
      coinRefunded
    }
  }
  ${REACHED_ENDING_DTO}
  ${USER_HAS_ENDING_DTO}
  ${NEXT_CHAPTER_DTO}
`;

export const SAVE_USER_HISTORY = gql`
  mutation SaveUserHistory($storyId: Int!) {
    createUserStoryHistory(data: { storyId: $storyId }) {
      storyId
    }
  }
`;

export const UPDATE_USER_ITEM = gql`
  mutation UpdateUserItems(
    $storyId: Int!
    $chapterId: Int!
    $updatedItem: String!
    $updatedUserItems: String
  ) {
    updateUserItems(
      data: {
        storyId: $storyId
        chapterId: $chapterId
        updatedItem: $updatedItem
        updatedUserItems: $updatedUserItems
      }
    ) {
      ...ItemDto
    }
  }
  ${ITEM_DTO}
`;

export const CHAPTER_ENDING = gql`
  query ChapterEnding($data: SingleIDInput!) {
    getStoryV3(data: $data) {
      lastPlay {
        currentPlayingChapter {
          endingId
        }
      }
    }
  }
`;

export const GET_USER_CHOICE_RESULT = gql`
  query GetUserChoiceResult($storyId: Int!, $choiceId: Int!, $chapterId: Int!) {
    getUserChoiceResult(
      data: { storyId: $storyId, choiceId: $choiceId, chapterId: $chapterId }
    ) {
      choiceResult {
        ...ChoiceResult
      }
      userSelectedChoice {
        choiceId
        selectedChoice
      }
    }
  }
  ${CHOICE_RESULT}
`;

export const GQL_CHOICES_FOR_CONTINUE = gql`
  query GetChoicesForContinue($chapterId: Int!) {
    getChoicesForContinue(data: { chapterId: $chapterId }) {
      choiceId
      bestChapterComments {
        totalCount
      }
    }
  }
`;

export const GET_STORY_BY_SHARE_KEY = gql`
  query GetStoryByShareKey($id: String!) {
    getStoryByShareKey(data: { id: $id }) {
      name
      authorName
      coverImageLink
      topic
      ageTarget
      desc
      storyId
    }
  }
`;

export const GET_USER_INPUT_CHOICE = gql`
  query GetUserInputChoice(
    $chapterId: Int!
    $choiceName: String!
    $leftUserInputText: String!
    $rightUserInputText: String!
  ) {
    getUserInputChoice(
      data: {
        chapterId: $chapterId
        choiceName: $choiceName
        leftUserInputText: $leftUserInputText
        rightUserInputText: $rightUserInputText
      }
    ) {
      choiceIndex
    }
  }
`;

export const GET_PRESIGNED_URLS = gql`
  query GetPresignedUrls(
    $storageType: Storages!
    $fileNames: [String!]!
    $isTemp: Boolean
  ) {
    getPresignedUrls(
      data: {
        storageType: $storageType
        fileNames: $fileNames
        isTemp: $isTemp
      }
    ) {
      url
      hashId
      fileName
      isTemp
      storageType
    }
  }
`;

export const REQUESTS_TTS = gql`
  mutation RequestTts(
    $text: String!
    $emotion: TTS_EMOTION!
    $speakerId: Int!
    $dpeId: TTS_DPE_ID!
    $accessToken: String
  ) {
    requestTts(
      data: {
        text: $text
        emotion: $emotion
        speakerId: $speakerId
        dpeId: $dpeId
        accessToken: $accessToken
      }
    ) {
      accessToken
    }
  }
`;

export const GET_TTS_URL = gql`
  mutation GetTtsUrl(
    $text: String!
    $emotion: TTS_EMOTION!
    $speakerId: Int!
    $dpeId: TTS_DPE_ID!
    $accessToken: String
  ) {
    getTtsUrl(
      data: {
        text: $text
        emotion: $emotion
        speakerId: $speakerId
        dpeId: $dpeId
        accessToken: $accessToken
      }
    ) {
      url
    }
  }
`;

export const SYNC_USER_ITEMS = gql`
  mutation syncUserItems(
    $storyId: Int!
    $chapterId: Int!
    $updatedUserItems: String
  ) {
    syncUserItems(
      data: {
        storyId: $storyId
        chapterId: $chapterId
        updatedUserItems: $updatedUserItems
      }
    ) {
      ok
    }
  }
`;

export const CHALLENGE_STORY_LIST = gql`
  query ChallengeStoryList(
    $sorting: CHALLENGE_STORY_SORTING
    $genre: String
    $page: Int
    $pageSize: Int
    $version: Int
  ) {
    listChallengeStoryByApp(
      data: {
        sorting: $sorting
        genre: $genre
        page: $page
        pageSize: $pageSize
        version: $version
      }
    ) {
      totalCount
      page
      pageSize
      version
      list {
        storyId
        mainImageFile {
          link
        }
        challengeStoryGenre {
          type
          text {
            ko
          }
        }
        isContest
        name
        oneLineDesc
        authorName
        updatedAt
        numLikes
        numComment
      }
    }
  }
`;

export const CHALLENGE_STORY_GENRE = gql`
  query ChallengeStoryGenres {
    genre: ugcStoryGenresV3 {
      type
      text {
        key
        ko_KR
      }
    }
    contest: getCurrentContest {
      version
    }
  }
`;

export const CURRENT_CONTEST = gql`
  query CurrentContest {
    getActiveBottomBannersForClient {
      bottomBannerId
      title
      styleTitle
      webLink
    }
  }
`;

export const PURCHASE_STORY = gql`
  mutation PurchaseStory($storyId: Int!, $price: Int!) {
    purchaseStory(data: { storyId: $storyId, price: $price }) {
      ok
    }
  }
`;

export const STORY_COMMENT_LIST = gql`
  query StoryCommentList(
    $storyId: Int
    $page: Int
    $pageSize: Int
    $orderBy: [ChapterCommentOrderBy!]
  ) {
    listChapterComment(
      data: {
        storyId: $storyId
        page: $page
        pageSize: $pageSize
        orderBy: $orderBy
      }
    ) {
      list {
        ...CommentDto
      }
      page
      totalCount
    }
  }
  ${COMMENT_DTO}
`;

export const CHAPTER_COMMENT_LIST = gql`
  query ChapterCommentList(
    $storyId: Int
    $chapterId: Int
    $page: Int
    $pageSize: Int
    $orderBy: [ChapterCommentOrderBy!]
    $exceptDeleted: Boolean
    $exceptHidden: Boolean
  ) {
    listChapterComment(
      data: {
        storyId: $storyId
        chapterId: $chapterId
        page: $page
        pageSize: $pageSize
        orderBy: $orderBy
        exceptDeleted: $exceptDeleted
        exceptHidden: $exceptHidden
      }
    ) {
      list {
        ...CommentDto
      }
      page
      totalCount
    }
  }
  ${COMMENT_DTO}
`;

export const LIKE_STORY = gql`
  mutation LikeStory($id: Int!) {
    likeStory(data: { id: $id }) {
      ok
    }
  }
`;

export const CANCEL_LIKE_STORY = gql`
  mutation cancelLikeStory($id: Int!) {
    cancelLikeStory(data: { id: $id }) {
      ok
    }
  }
`;

// #####
// ## Create AI Character
// #####
export const CREATE_AI_CHARACTER = gql`
  mutation createAICharacter(
    $storyId: Int!
    $image: Upload!
    $chrId: Int!
    $sessionId: String!
    $useCoin: Int
    $viewAd: Boolean!
    $additionalPromptIds: [Int!]
    $tossPaymentsId: Int
  ) {
    requestAICharacter(
      data: {
        storyId: $storyId
        image: $image
        chrId: $chrId
        sessionId: $sessionId
        useCoin: $useCoin
        viewAd: $viewAd
        additionalPromptIds: $additionalPromptIds
        tossPaymentsId: $tossPaymentsId
      }
    ) {
      sessionId
      request {
        id
        status
        imageFile {
          fileId
          link
        }
      }
    }
  }
`;

// #####
// ## Create Storygame Character
// #####
export const CREATE_STORYGAME_CHARACTER = gql`
  mutation createStorygameCharacter(
    $chrId: Int!
    $storyId: Int!
    $portraitImageId: Int!
    $image: Upload!
    $firstName: String
    $lastName: String
  ) {
    createStorygameCharacter(
      data: {
        chrId: $chrId
        storyId: $storyId
        portraitImageId: $portraitImageId
        image: $image
        firstName: $firstName
        lastName: $lastName
      }
    ) {
      id
      portraitFile {
        filedId
        link
      }
      imageFile {
        filedId
        link
      }
    }
  }
`;

export const GET_SESSION_ID = gql`
  query getSessionId {
    requestSession {
      sessionId
    }
  }
`;

// #####
// ## List MainCharacter
// #####
export const LIST_MAIN_CHARACTER = gql`
  query listMainCharacter($storyId: Int!) {
    listMainCharacters(data: { storyId: $storyId }) {
      list {
        id
        displayName
        name
        firstName
        lastName
        gender
        isCustom
        requestId
        viewAdCount
        latestCreatedAt
        imageFile {
          fileId
          link
        }
        portraitFile {
          fileId
          link
        }
      }
    }
  }
`;

// #####
// ## Get Additional Prompts
// #####
export const GET_PROMPTS = gql`
  query getPrompts($storyId: Int!, $gender: GENDER!) {
    getStorygameAdditionalPrompts(
      data: { storyId: $storyId, gender: $gender }
    ) {
      prompts
    }
  }
`;

// #####
// ## Get List Request Character
// #####
export const LIST_GENERATED_IMAGE_REQUEST = gql`
  query listRequest($sessionId: String!, $chrId: Int!) {
    listRequestAICharacter(data: { sessionId: $sessionId, chrId: $chrId }) {
      chrId
      list {
        id
        status
        imageFile {
          fileId
          link
        }
      }
    }
  }
`;

export const UPSERT_STORYGAME_CHARACTER = gql`
  mutation UpsertStorygameCharacter(
    $storyId: Int!
    $chrId: Int!
    $firstName: String
    $lastName: String
    $portraitImageId: Int
    $image: Upload
    $imageId: Int
  ) {
    upsertStorygameCharacter(
      data: {
        storyId: $storyId
        chrId: $chrId
        firstName: $firstName
        lastName: $lastName
        portraitImageId: $portraitImageId
        image: $image
        imageId: $imageId
      }
    ) {
      id
      storyId
      chrId
      firstName
      lastName
      portraitFileId
      imageFileId
      imageFile {
        fileId
        link
      }
    }
  }
`;

export const GET_SELECTED_REQUEST_AI_CHARACTER = gql`
  query GetSelectedRequestAICharacter($storyId: Int!, $chrId: Int!) {
    getSelectedRequestAICharacter(data: { storyId: $storyId, chrId: $chrId }) {
      id
      chrId
      imageFile {
        fileId
        link
      }
    }
  }
`;

export const CONFIRM_FAILED_REQUEST_AI_CHARACTER = gql`
  mutation ConfirmFailedRequestAICharacter($id: Int!) {
    confirmFailedRequestAICharacter(data: { id: $id }) {
      ok
    }
  }
`;

// #####
// ## Get Storygame Character 스토리게임 광고 리셋 API
// #####
export const RESTART_AI_CHARACTER_STORYGAME_CHAPTER = gql`
  mutation RestartAICharacterStorygameChapter($chapterId: Int!) {
    restartAICharacterStorygameChapter(data: { id: $chapterId }) {
      ok
    }
  }
`;
